export type StudioLaunchAccessInput = {
  studioEarlyAccessGrantedAt: Date | null | undefined;
  studioLaunchAt: Date | null | undefined;
  now?: Date;
  /** When true, Studio stays locked until STUDIO_LAUNCH_AT is configured (staging/production). */
  requireLaunchDate?: boolean;
};

/** Parse ISO launch timestamp from platform config. Invalid values are treated as unset. */
export function parseStudioLaunchAt(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;
  const parsed = new Date(value.trim());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Studio dashboard/modules are usable when launch time has passed or admin granted early access.
 * When launch time is unset, Studio stays open in dev; staging/production require a launch date.
 */
export function canUseStudioApp(input: StudioLaunchAccessInput): boolean {
  if (input.studioEarlyAccessGrantedAt) return true;
  if (!input.studioLaunchAt) return !input.requireLaunchDate;
  const now = input.now ?? new Date();
  return now.getTime() >= input.studioLaunchAt.getTime();
}

export function isStudioLaunchDateRequired(nodeEnv: string | null | undefined): boolean {
  const normalized = (nodeEnv ?? 'development').trim().toLowerCase();
  return normalized === 'production' || normalized === 'staging';
}

export type StudioLaunchCountdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
};

export function studioLaunchCountdown(
  studioLaunchAt: Date,
  now: Date = new Date(),
): StudioLaunchCountdown {
  const diffMs = studioLaunchAt.getTime() - now.getTime();
  if (diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, isPast: false };
}
